import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  Users, FileText, CurrencyDollar, ChartLineUp, CheckCircle, XCircle, 
  Clock, Crown, Bookmark, TrendUp, Lightning, Target, UserCircle, Sparkle, Megaphone, Buildings, Hourglass, Fire, Eye, Timer, GlobeHemisphereWest
} from "@phosphor-icons/react";

const StatCard = ({ icon: Icon, label, value, sub, accent }) => (
  <Card className="bg-zinc-950 border-zinc-800" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-zinc-500 uppercase tracking-widest mb-1">{label}</p>
          <p className={`text-2xl font-bold ${accent ? 'text-emerald-400' : ''}`}>{value}</p>
          {sub && <p className="text-xs text-zinc-500 mt-0.5">{sub}</p>}
        </div>
        <Icon weight="duotone" className={`w-9 h-9 ${accent ? 'text-emerald-500/40' : 'text-zinc-700'}`} />
      </div>
    </CardContent>
  </Card>
);

const ProgressBar = ({ value, max, color = "bg-emerald-500" }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="w-full bg-zinc-800 rounded-full h-2">
      <div className={`${color} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
};

const UtmList = ({ label, rows, testid }) => {
  const items = Array.isArray(rows) ? rows.slice(0, 5) : [];
  return (
    <div data-testid={testid}>
      <p className="text-[10px] text-zinc-500 uppercase tracking-widest mb-1.5 font-mono">{label}</p>
      {items.length > 0 ? (
        <ul className="space-y-1">
          {items.map((r, i) => (
            <li key={i} className="flex items-center justify-between text-xs">
              <span className="text-zinc-300 truncate pr-2">{r.value}</span>
              <span className="font-mono text-zinc-500">{r.sessions}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[11px] text-zinc-600 italic">No tagged traffic yet.</p>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trialWatch, setTrialWatch] = useState(null);
  const [siteAnalytics, setSiteAnalytics] = useState(null);
  const [funnel, setFunnel] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [statsRes, trialsRes, analyticsRes, funnelRes] = await Promise.all([
          axios.get(`${API}/admin/stats`),
          axios.get(`${API}/admin/trials/active`).catch(() => ({ data: null })),
          axios.get(`${API}/admin/site-analytics`).catch(() => ({ data: null })),
          axios.get(`${API}/admin/funnel?window_days=30`).catch(() => ({ data: null })),
        ]);
        setStats(statsRes.data);
        setTrialWatch(trialsRes.data);
        setSiteAnalytics(analyticsRes.data);
        setFunnel(funnelRes.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner" />
        </div>
      </AdminLayout>
    );
  }

  if (!stats) {
    return (
      <AdminLayout>
        <p className="text-zinc-500">Failed to load analytics.</p>
      </AdminLayout>
    );
  }

  const totalApps = (stats.applications?.draft || 0) + (stats.applications?.submitted || 0) + 
                    (stats.applications?.approved || 0) + (stats.applications?.rejected || 0);

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-dashboard-page">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Analytics Dashboard</h1>
          <p className="text-zinc-400 mt-1">Platform performance and insights</p>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-2" data-testid="admin-quick-actions">
          <button
            type="button"
            onClick={() => navigate("/admin/client-sprints")}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-emerald-600 border border-emerald-500 text-black text-sm font-bold hover:bg-emerald-500 transition-colors"
            data-testid="quick-action-client-sprints"
          >
            <Lightning weight="fill" className="w-4 h-4" />
            Client Setup Sprints
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/announcements")}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-sm font-semibold hover:bg-emerald-500/20 transition-colors"
            data-testid="quick-action-announcements"
          >
            <Megaphone weight="fill" className="w-4 h-4" />
            Send Announcement
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/follow-ups")}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-sm font-semibold hover:bg-emerald-500/20 transition-colors"
            data-testid="quick-action-follow-ups"
          >
            <Lightning weight="fill" className="w-4 h-4" />
            Follow-ups Inbox
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/funder-status")}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-amber-500/10 border border-amber-500/40 text-amber-300 text-sm font-semibold hover:bg-amber-500/20 transition-colors"
            data-testid="quick-action-funder-status"
          >
            <Lightning weight="fill" className="w-4 h-4" />
            Funder Status Sweep
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/verification-queue")}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-sm font-semibold hover:bg-emerald-500/20 transition-colors"
            data-testid="quick-action-verification-queue"
          >
            <Lightning weight="fill" className="w-4 h-4" />
            Verification Queue
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/users")}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-200 text-sm font-semibold hover:border-zinc-700 transition-colors"
            data-testid="quick-action-users"
          >
            <Users className="w-4 h-4" />
            Manage Users
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/grants")}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-200 text-sm font-semibold hover:border-zinc-700 transition-colors"
            data-testid="quick-action-grants"
          >
            <Lightning className="w-4 h-4" />
            Manage Grants
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/corporate-leads")}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-200 text-sm font-semibold hover:border-zinc-700 transition-colors"
            data-testid="quick-action-corp-leads"
          >
            <Buildings className="w-4 h-4" />
            Corporate Leads
          </button>
          <button
            type="button"
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/admin/scout/link-health`);
                const data = res.data;
                let msg = `Checked ${data.urls_checked} URLs across ${data.scouts_checked} scouts.\n\n`;
                if (data.broken_count === 0) {
                  msg += "All real URLs returned 200 OK. ✅";
                } else {
                  msg += `${data.broken_count} broken URLs:\n\n` +
                    data.broken.map(b => `• ${b.company_name} (${b.field}): ${b.status}\n   ${b.url}`).join("\n\n");
                }
                if (data.bot_blocked_count > 0) {
                  msg += `\n\n${data.bot_blocked_count} URLs blocked our bot (likely CDN protection — verify in browser before assuming broken):\n\n` +
                    data.bot_blocked.map(b => `• ${b.company_name}: ${b.url}`).join("\n");
                }
                alert(msg);
              } catch (e) {
                alert("Link health check failed: " + (e.response?.data?.detail || e.message));
              }
            }}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-200 text-sm font-semibold hover:border-zinc-700 transition-colors"
            data-testid="quick-action-link-health"
          >
            <CheckCircle className="w-4 h-4" />
            Check Scout Links
          </button>
        </div>

        {/* Trial Watch — expiring soon banner + active trials list */}
        {trialWatch && trialWatch.active_count > 0 && (
          <Card
            className={`border-2 ${
              trialWatch.critical_count > 0
                ? "bg-red-500/5 border-red-500/40"
                : trialWatch.expiring_soon_count > 0
                ? "bg-amber-500/5 border-amber-500/40"
                : "bg-emerald-500/5 border-emerald-500/30"
            }`}
            data-testid="trial-watch-panel"
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                {trialWatch.critical_count > 0 ? (
                  <Fire weight="fill" className="w-5 h-5 text-red-400" />
                ) : (
                  <Hourglass weight="fill" className="w-5 h-5 text-amber-400" />
                )}
                Trial Watch
                <span className="ml-2 text-xs font-normal text-zinc-400">
                  {trialWatch.active_count} active
                  {trialWatch.critical_count > 0 && (
                    <span className="text-red-400 font-semibold ml-2">
                      · {trialWatch.critical_count} expiring in ≤2 days
                    </span>
                  )}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {trialWatch.critical_count > 0 && (
                <div className="mb-3 px-3 py-2 rounded-sm bg-red-500/10 border border-red-500/30 text-sm text-red-200" data-testid="trial-watch-alert">
                  <strong>Action needed:</strong> {trialWatch.critical_count} trial{trialWatch.critical_count !== 1 ? "s" : ""} ending in ≤2 days. Reach out today to convert them to paid.
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="trial-watch-table">
                  <thead>
                    <tr className="text-left text-xs text-zinc-500 uppercase tracking-wider border-b border-zinc-800">
                      <th className="pb-2 pr-3">Name</th>
                      <th className="pb-2 pr-3">Email</th>
                      <th className="pb-2 pr-3">Started</th>
                      <th className="pb-2 pr-3">Day</th>
                      <th className="pb-2 pr-3">Ends</th>
                      <th className="pb-2 pr-3">Left</th>
                      <th className="pb-2 pr-3">Activity</th>
                      <th className="pb-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {trialWatch.trials.map((t) => {
                      const urg = t.urgency;
                      const badge = urg === "critical"
                        ? "bg-red-500/20 text-red-300 border border-red-500/40"
                        : urg === "warning"
                        ? "bg-amber-500/20 text-amber-300 border border-amber-500/40"
                        : urg === "expired"
                        ? "bg-zinc-700/40 text-zinc-400 border border-zinc-700"
                        : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40";
                      const startedStr = t.trial_started_at
                        ? new Date(t.trial_started_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
                        : "—";
                      const endsStr = t.trial_ends_at
                        ? new Date(t.trial_ends_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
                        : "—";
                      const leftLabel = t.days_left > 0
                        ? `${t.days_left}d ${t.hours_left}h`
                        : t.days_left === 0
                        ? `${t.hours_left}h`
                        : `Expired`;
                      return (
                        <tr key={t.user_id} className="border-b border-zinc-800/50 hover:bg-zinc-900/50" data-testid={`trial-row-${t.user_id}`}>
                          <td className="py-2.5 pr-3 font-medium">
                            <div>{t.name || "Unknown"}</div>
                            {t.org_name && <div className="text-xs text-zinc-500">{t.org_name}</div>}
                          </td>
                          <td className="py-2.5 pr-3 text-zinc-400 break-all">{t.email}</td>
                          <td className="py-2.5 pr-3 text-zinc-400">{startedStr}</td>
                          <td className="py-2.5 pr-3">
                            <span className="font-mono text-zinc-300">{t.day_number || "—"}/14</span>
                          </td>
                          <td className="py-2.5 pr-3 text-zinc-400">{endsStr}</td>
                          <td className="py-2.5 pr-3">
                            <span className={`inline-block px-2 py-0.5 rounded-sm text-xs font-semibold ${badge}`} data-testid={`trial-urgency-${t.user_id}`}>
                              {leftLabel}
                            </span>
                          </td>
                          <td className="py-2.5 pr-3 text-xs text-zinc-500">
                            {t.apps_used_this_month || 0} apps · {t.drafts_used_this_month || 0} drafts
                            {t.has_profile && <span className="ml-1 text-emerald-400">· profile ✓</span>}
                          </td>
                          <td className="py-2.5">
                            <button
                              type="button"
                              onClick={() => navigate(`/admin/users?focus=${t.user_id}`)}
                              className="px-2.5 py-1 rounded-sm bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs font-semibold hover:bg-emerald-500/25 transition-colors"
                              data-testid={`trial-open-${t.user_id}`}
                            >
                              Open →
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Site Analytics — visitors & time on site (in-house tracker) */}
        {siteAnalytics && (
          <Card className="bg-zinc-950 border-zinc-800" data-testid="site-analytics-panel">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <GlobeHemisphereWest weight="fill" className="w-5 h-5 text-sky-400" />
                Site Analytics
                <span className="ml-2 text-xs font-normal text-zinc-500">
                  Anonymous visitor tracking · excludes admin sessions
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Top-line stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="rounded-sm bg-zinc-900/60 border border-zinc-800 p-3" data-testid="analytics-visitors-today">
                  <p className="text-[10px] text-zinc-500 uppercase tracking-widest">Visitors Today</p>
                  <p className="text-2xl font-bold mt-1">{siteAnalytics.unique_visitors?.today ?? 0}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">{siteAnalytics.visits?.today ?? 0} page views</p>
                </div>
                <div className="rounded-sm bg-zinc-900/60 border border-zinc-800 p-3" data-testid="analytics-visitors-7d">
                  <p className="text-[10px] text-zinc-500 uppercase tracking-widest">Visitors · 7d</p>
                  <p className="text-2xl font-bold mt-1">{siteAnalytics.unique_visitors?.last_7d ?? 0}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">{siteAnalytics.visits?.last_7d ?? 0} page views</p>
                </div>
                <div className="rounded-sm bg-zinc-900/60 border border-zinc-800 p-3" data-testid="analytics-visitors-30d">
                  <p className="text-[10px] text-zinc-500 uppercase tracking-widest">Visitors · 30d</p>
                  <p className="text-2xl font-bold mt-1">{siteAnalytics.unique_visitors?.last_30d ?? 0}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">{siteAnalytics.visits?.last_30d ?? 0} page views</p>
                </div>
                <div className="rounded-sm bg-sky-500/5 border border-sky-500/30 p-3" data-testid="analytics-avg-session">
                  <p className="text-[10px] text-sky-300/70 uppercase tracking-widest flex items-center gap-1">
                    <Timer weight="fill" className="w-3 h-3" />
                    Avg Session
                  </p>
                  <p className="text-2xl font-bold mt-1 text-sky-300">{siteAnalytics.avg_session_display || "0m 0s"}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">{siteAnalytics.total_sessions_30d || 0} sessions · 30d</p>
                </div>
              </div>

              {/* Two-column: Top pages + Conversion */}
              <div className="grid lg:grid-cols-2 gap-4">
                {/* Top pages */}
                <div data-testid="analytics-top-pages">
                  <p className="text-xs text-zinc-500 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                    <Eye weight="fill" className="w-3 h-3" />
                    Top Pages · 30d
                  </p>
                  {siteAnalytics.top_pages && siteAnalytics.top_pages.length > 0 ? (
                    <ul className="space-y-1.5">
                      {siteAnalytics.top_pages.map((p, i) => {
                        const max = siteAnalytics.top_pages[0].views;
                        const pct = max > 0 ? (p.views / max) * 100 : 0;
                        return (
                          <li key={i} className="text-sm">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-mono text-zinc-300 truncate pr-2">{p.path}</span>
                              <span className="font-mono text-zinc-400 text-xs">{p.views}</span>
                            </div>
                            <div className="w-full bg-zinc-800 rounded-full h-1.5">
                              <div className="bg-sky-500/70 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-sm text-zinc-500 italic">No traffic recorded yet. Visit the site as a non-admin to start tracking.</p>
                  )}
                </div>

                {/* Conversion */}
                <div data-testid="analytics-conversion">
                  <p className="text-xs text-zinc-500 uppercase tracking-widest mb-2">Visitor → Signup · 30d</p>
                  <div className="rounded-sm bg-emerald-500/5 border border-emerald-500/30 p-4">
                    <div className="flex items-baseline gap-3">
                      <p className="text-3xl font-bold text-emerald-300">{siteAnalytics.conversion_rate ?? 0}%</p>
                      <p className="text-xs text-zinc-400">
                        {siteAnalytics.signups_30d ?? 0} signups from {siteAnalytics.unique_visitors?.last_30d ?? 0} unique visitors
                      </p>
                    </div>
                    <p className="text-[11px] text-zinc-500 mt-2">
                      Industry benchmark for SaaS landing pages: 2–5%. Anything above 3% is strong.
                    </p>
                  </div>
                </div>
              </div>

              {/* ---- Traffic Sources (UTM attribution) ---- */}
              <div className="pt-4 border-t border-zinc-800" data-testid="analytics-traffic-sources">
                <div className="flex items-baseline justify-between mb-3">
                  <p className="text-xs text-zinc-500 uppercase tracking-widest">Traffic Sources · 30d</p>
                  <p className="text-[10px] text-zinc-600 italic">
                    Tag links with{" "}
                    <code className="text-zinc-400">?utm_source=linkedin&utm_medium=post&utm_campaign=launch</code>
                  </p>
                </div>

                <div className="grid lg:grid-cols-2 gap-4">
                  {/* Channels (combined view) */}
                  <div>
                    <p className="text-[11px] text-zinc-400 mb-2 font-semibold">All channels</p>
                    {siteAnalytics.top_channels && siteAnalytics.top_channels.length > 0 ? (
                      <ul className="space-y-1.5">
                        {siteAnalytics.top_channels.map((c, i) => {
                          const max = siteAnalytics.top_channels[0].sessions;
                          const pct = max > 0 ? (c.sessions / max) * 100 : 0;
                          const isDirect = c.channel === "direct";
                          return (
                            <li key={i} className="text-sm" data-testid={`channel-row-${i}`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className={`truncate pr-2 ${isDirect ? "text-zinc-500 italic" : "text-zinc-300"}`}>
                                  {c.channel}
                                </span>
                                <span className="font-mono text-zinc-400 text-xs">{c.sessions}</span>
                              </div>
                              <div className="w-full bg-zinc-800 rounded-full h-1.5">
                                <div className={`h-1.5 rounded-full ${isDirect ? "bg-zinc-600" : "bg-violet-500/70"}`} style={{ width: `${pct}%` }} />
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p className="text-xs text-zinc-500 italic">No sessions tracked yet.</p>
                    )}
                  </div>

                  {/* UTM Source / Medium / Campaign breakdown */}
                  <div className="space-y-3">
                    <UtmList label="utm_source" rows={siteAnalytics.top_utm_sources} testid="utm-sources" />
                    <UtmList label="utm_medium" rows={siteAnalytics.top_utm_mediums} testid="utm-mediums" />
                    <UtmList label="utm_campaign" rows={siteAnalytics.top_utm_campaigns} testid="utm-campaigns" />
                  </div>
                </div>

                <p className="text-[11px] text-zinc-600 mt-3 leading-relaxed">
                  <b className="text-zinc-500">Why this matters:</b> mobile clicks from LinkedIn, texts, and emails strip referrers, so untagged traffic shows as <i>direct</i>. UTM-tagged links give you real attribution.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Conversion Funnel — per-source autopsy (sessions → signups → trials → paid) */}
        {funnel && (
          <Card className="bg-zinc-950 border-zinc-800" data-testid="funnel-panel">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Target weight="fill" className="w-5 h-5 text-amber-400" />
                Conversion Funnel · {funnel.window_days}d
                <span className="ml-2 text-xs font-normal text-zinc-500">
                  Sessions → Signup → Trial → Paid, broken down by traffic source
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Totals strip */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="funnel-totals">
                <div className="rounded-sm bg-zinc-900/60 border border-zinc-800 p-3">
                  <p className="text-[10px] text-zinc-500 uppercase tracking-widest">Sessions</p>
                  <p className="text-2xl font-bold mt-1">{funnel.totals.sessions}</p>
                </div>
                <div className="rounded-sm bg-zinc-900/60 border border-zinc-800 p-3">
                  <p className="text-[10px] text-zinc-500 uppercase tracking-widest">Signups</p>
                  <p className="text-2xl font-bold mt-1">{funnel.totals.signups}</p>
                </div>
                <div className="rounded-sm bg-zinc-900/60 border border-zinc-800 p-3">
                  <p className="text-[10px] text-zinc-500 uppercase tracking-widest">Trials</p>
                  <p className="text-2xl font-bold mt-1">{funnel.totals.trial_starts}</p>
                </div>
                <div className="rounded-sm bg-emerald-500/5 border border-emerald-500/30 p-3">
                  <p className="text-[10px] text-emerald-300/70 uppercase tracking-widest">Paid</p>
                  <p className="text-2xl font-bold mt-1 text-emerald-300">{funnel.totals.paid}</p>
                </div>
                <div className="rounded-sm bg-amber-500/5 border border-amber-500/30 p-3">
                  <p className="text-[10px] text-amber-300/70 uppercase tracking-widest">Site Signup Rate</p>
                  <p className="text-2xl font-bold mt-1 text-amber-300">{funnel.totals.signup_rate}%</p>
                </div>
              </div>

              {/* Per-source breakdown */}
              {funnel.rows && funnel.rows.length > 0 ? (
                <>
                  {/* Desktop: full table */}
                  <div className="hidden lg:block overflow-x-auto" data-testid="funnel-table-wrap">
                    <table className="w-full text-sm" data-testid="funnel-table">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                          <th className="py-2 pr-3">Source</th>
                          <th className="py-2 pr-3 text-right">Sessions</th>
                          <th className="py-2 pr-3 text-right">Signups</th>
                          <th className="py-2 pr-3 text-right">Signup %</th>
                          <th className="py-2 pr-3 text-right">Trials</th>
                          <th className="py-2 pr-3 text-right">Paid</th>
                        </tr>
                      </thead>
                      <tbody>
                        {funnel.rows.map((r, i) => {
                          const rateColor =
                            r.sessions === 0 ? "text-zinc-600" :
                            r.signup_rate >= 3 ? "text-emerald-400" :
                            r.signup_rate >= 1 ? "text-amber-400" :
                            "text-red-400";
                          return (
                            <tr key={i} className="border-b border-zinc-900 hover:bg-zinc-900/40" data-testid={`funnel-row-${i}`}>
                              <td className="py-2 pr-3">
                                <span className={r.is_tagged ? "text-zinc-200" : "text-zinc-500 italic"}>
                                  {r.source}
                                </span>
                              </td>
                              <td className="py-2 pr-3 text-right font-mono text-zinc-300">{r.sessions}</td>
                              <td className="py-2 pr-3 text-right font-mono text-zinc-300">{r.signups}</td>
                              <td className={`py-2 pr-3 text-right font-mono font-bold ${rateColor}`}>{r.signup_rate}%</td>
                              <td className="py-2 pr-3 text-right font-mono text-zinc-400">{r.trial_starts}</td>
                              <td className="py-2 pr-3 text-right font-mono text-emerald-400">{r.paid}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile: stacked cards */}
                  <div className="lg:hidden space-y-2" data-testid="funnel-cards-mobile">
                    {funnel.rows.map((r, i) => {
                      const rateColor =
                        r.sessions === 0 ? "text-zinc-600" :
                        r.signup_rate >= 3 ? "text-emerald-400" :
                        r.signup_rate >= 1 ? "text-amber-400" :
                        "text-red-400";
                      const rateBorder =
                        r.sessions === 0 ? "border-zinc-800" :
                        r.signup_rate >= 3 ? "border-emerald-500/40" :
                        r.signup_rate >= 1 ? "border-amber-500/40" :
                        "border-red-500/40";
                      return (
                        <div
                          key={i}
                          className={`rounded-sm bg-zinc-900/40 border ${rateBorder} p-3`}
                          data-testid={`funnel-row-${i}`}
                        >
                          <div className="flex items-center justify-between mb-2 pb-2 border-b border-zinc-800">
                            <span className={`text-sm font-semibold ${r.is_tagged ? "text-zinc-100" : "text-zinc-500 italic"}`}>
                              {r.source}
                            </span>
                            <span className={`text-xl font-mono font-bold ${rateColor}`}>{r.signup_rate}%</span>
                          </div>
                          <div className="grid grid-cols-4 gap-2 text-center">
                            <div>
                              <p className="text-[9px] text-zinc-500 uppercase tracking-widest">Sessions</p>
                              <p className="text-sm font-mono text-zinc-300 mt-0.5">{r.sessions}</p>
                            </div>
                            <div>
                              <p className="text-[9px] text-zinc-500 uppercase tracking-widest">Signups</p>
                              <p className="text-sm font-mono text-zinc-300 mt-0.5">{r.signups}</p>
                            </div>
                            <div>
                              <p className="text-[9px] text-zinc-500 uppercase tracking-widest">Trials</p>
                              <p className="text-sm font-mono text-zinc-400 mt-0.5">{r.trial_starts}</p>
                            </div>
                            <div>
                              <p className="text-[9px] text-zinc-500 uppercase tracking-widest">Paid</p>
                              <p className="text-sm font-mono text-emerald-400 mt-0.5">{r.paid}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <p className="text-sm text-zinc-500 italic" data-testid="funnel-empty">
                  No traffic in the last {funnel.window_days} days yet. UTM-tag your outbound links to populate this table.
                </p>
              )}

              <div className="rounded-sm bg-zinc-900/40 border border-zinc-800 p-3 text-[11px] text-zinc-400 leading-relaxed" data-testid="funnel-legend">
                <p>
                  <b className="text-zinc-300">How to read this:</b> the <i className="text-zinc-200">untagged</i> row is everyone who landed without a UTM source (direct, organic search, SMS, most social mobile). To shrink it, tag every outbound link you control — emails, LinkedIn posts, business cards (QR), Founder Slides links.
                </p>
                <p className="mt-1.5">
                  <span className="text-emerald-400">Green ≥3%</span> · <span className="text-amber-400">Amber 1–3%</span> · <span className="text-red-400">Red &lt;1%</span> signup rate. Sources sending lots of sessions with red signup rates are leaking visitors — fix the landing page promise or the message that drove them.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Top Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Total Users" value={stats.total_users} sub={`${stats.recent_activity?.new_users_7d || 0} new this week`} />
          <StatCard icon={CurrencyDollar} label="Est. MRR" value={`$${(stats.estimated_mrr ?? 0).toLocaleString()}`} sub={`${stats.paying_users ?? 0} paying customer${(stats.paying_users ?? 0) === 1 ? "" : "s"} (Stripe active)`} accent />
          <StatCard icon={FileText} label="Applications" value={stats.total_applications} sub={`${stats.recent_activity?.new_apps_7d || 0} this week`} />
          <StatCard icon={Lightning} label="Active Grants" value={stats.total_grants} sub={`${stats.recent_activity?.new_grants_7d || 0} new this week`} />
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Bookmark} label="Bookmarks" value={stats.total_bookmarks} sub={`${stats.engagement?.avg_bookmarks || 0} avg per user`} />
          <StatCard icon={ChartLineUp} label="Post-Grants" value={stats.total_post_grants} />
          <StatCard icon={Sparkle} label="Flash Drafts Used" value={stats.engagement?.flash_drafts_used || 0} />
          <StatCard icon={Target} label="Conversion Rate" value={`${stats.engagement?.conversion_rate || 0} apps/user`} />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Subscription Breakdown */}
          <Card className="bg-zinc-950 border-zinc-800" data-testid="subscription-breakdown">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Crown weight="fill" className="w-4 h-4 text-emerald-400" />
                Subscriptions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-zinc-400">Free</span>
                  <span className="text-sm font-mono">{stats.subscriptions?.free || 0}</span>
                </div>
                <ProgressBar value={stats.subscriptions?.free || 0} max={stats.total_users} color="bg-zinc-600" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-emerald-400">Premium</span>
                  <span className="text-sm font-mono text-emerald-400">{stats.subscriptions?.pro || 0}</span>
                </div>
                <ProgressBar value={stats.subscriptions?.pro || 0} max={stats.total_users} color="bg-emerald-500" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-amber-400">Admins</span>
                  <span className="text-sm font-mono text-amber-400">{stats.admin_users || 0}</span>
                </div>
                <ProgressBar value={stats.admin_users || 0} max={stats.total_users} color="bg-amber-500" />
              </div>
              <div className="pt-2 border-t border-zinc-800">
                <div className="flex justify-between text-xs text-zinc-500">
                  <span>Premium conversion</span>
                  <span className="text-emerald-400 font-medium">
                    {stats.total_users > 0 ? Math.round(((stats.subscriptions?.pro || 0) / stats.total_users) * 100) : 0}%
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Application Status */}
          <Card className="bg-zinc-950 border-zinc-800" data-testid="application-status">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText weight="fill" className="w-4 h-4 text-zinc-400" />
                Applications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { label: "Draft", value: stats.applications?.draft || 0, icon: FileText, color: "text-zinc-400", bar: "bg-zinc-600" },
                { label: "Submitted", value: stats.applications?.submitted || 0, icon: Clock, color: "text-blue-400", bar: "bg-blue-500" },
                { label: "Approved", value: stats.applications?.approved || 0, icon: CheckCircle, color: "text-emerald-400", bar: "bg-emerald-500" },
                { label: "Rejected", value: stats.applications?.rejected || 0, icon: XCircle, color: "text-red-400", bar: "bg-red-500" }
              ].map(({ label, value, icon: Icon, color, bar }) => (
                <div key={label}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-3.5 h-3.5 ${color}`} />
                      <span className="text-sm text-zinc-400">{label}</span>
                    </div>
                    <span className="text-sm font-mono">{value}</span>
                  </div>
                  <ProgressBar value={value} max={totalApps} color={bar} />
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Profile Completion */}
          <Card className="bg-zinc-950 border-zinc-800" data-testid="profile-completion">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <UserCircle weight="fill" className="w-4 h-4 text-zinc-400" />
                Profile Completion
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center py-4">
                <div className="relative w-32 h-32">
                  <svg className="w-32 h-32 -rotate-90" viewBox="0 0 120 120">
                    <circle cx="60" cy="60" r="50" fill="none" stroke="#27272a" strokeWidth="10" />
                    <circle cx="60" cy="60" r="50" fill="none" stroke="#10b981" strokeWidth="10" strokeLinecap="round"
                      strokeDasharray={`${((stats.profile_completion?.with_profile || 0) / Math.max(stats.total_users, 1)) * 314} 314`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold text-emerald-400">
                      {stats.total_users > 0 ? Math.round(((stats.profile_completion?.with_profile || 0) / stats.total_users) * 100) : 0}%
                    </span>
                  </div>
                </div>
              </div>
              <div className="space-y-2 mt-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">With profile</span>
                  <span className="font-mono text-emerald-400">{stats.profile_completion?.with_profile || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Without profile</span>
                  <span className="font-mono text-zinc-500">{stats.profile_completion?.without_profile || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Category Distribution */}
          <Card className="bg-zinc-950 border-zinc-800" data-testid="category-distribution">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Grant Categories</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(stats.category_distribution || []).map((cat) => (
                  <div key={cat.category}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-zinc-400 truncate max-w-[200px]">{cat.category}</span>
                      <span className="text-xs font-mono text-zinc-500">{cat.count}</span>
                    </div>
                    <ProgressBar value={cat.count} max={(stats.category_distribution || [])[0]?.count || 1} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Top Grants */}
          <Card className="bg-zinc-950 border-zinc-800" data-testid="top-grants">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendUp weight="fill" className="w-4 h-4 text-emerald-400" />
                Most Applied Grants
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(stats.top_grants || []).length === 0 ? (
                <p className="text-zinc-500 text-sm py-4 text-center">No applications yet</p>
              ) : (
                <div className="space-y-3">
                  {(stats.top_grants || []).map((g, i) => (
                    <button
                      key={g.grant_id}
                      onClick={() => navigate(`/grants/${g.grant_id}`)}
                      className="w-full text-left flex items-center gap-3 p-2 rounded-sm hover:bg-zinc-900 transition-colors"
                      data-testid={`top-grant-${i}`}
                    >
                      <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
                        i === 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-800 text-zinc-500'
                      }`}>{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{g.title}</p>
                        <p className="text-xs text-zinc-500">{g.applications} application{g.applications !== 1 ? 's' : ''}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Users */}
        <Card className="bg-zinc-950 border-zinc-800" data-testid="recent-users">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Recent Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-zinc-500 uppercase tracking-wider border-b border-zinc-800">
                    <th className="pb-3 pr-4">Name</th>
                    <th className="pb-3 pr-4">Email</th>
                    <th className="pb-3 pr-4">Tier</th>
                    <th className="pb-3 pr-4">Role</th>
                    <th className="pb-3">Joined</th>
                  </tr>
                </thead>
                <tbody>
                {(stats.recent_users || []).map((u) => {
                    const onTrial = (u.trial_status === "active" || u.trial_status === "trialing") && u.trial_ends_at;
                    let trialDaysLeft = null;
                    if (onTrial) {
                      const ms = new Date(u.trial_ends_at).getTime() - Date.now();
                      trialDaysLeft = Math.max(0, Math.ceil(ms / 86400000));
                    }
                    return (
                      <tr key={u.user_id} className="border-b border-zinc-800/50 hover:bg-zinc-900/50">
                        <td className="py-2.5 pr-4 font-medium">{u.name || 'Unknown'}</td>
                        <td className="py-2.5 pr-4 text-zinc-400">{u.email}</td>
                        <td className="py-2.5 pr-4">
                          {onTrial ? (
                            <span
                              className={`text-xs px-2 py-0.5 rounded-sm border ${
                                trialDaysLeft <= 2
                                  ? 'bg-red-500/15 text-red-300 border-red-500/40'
                                  : trialDaysLeft <= 5
                                  ? 'bg-amber-500/15 text-amber-300 border-amber-500/40'
                                  : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                              }`}
                              data-testid={`recent-user-trial-${u.user_id}`}
                            >
                              Trial · {trialDaysLeft}d left
                            </span>
                          ) : (
                            <span className={`text-xs px-2 py-0.5 rounded-sm ${
                              u.subscription_tier === 'pro' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-800 text-zinc-400'
                            }`}>
                              {u.subscription_tier}
                              {u.subscription_permanent && ' ★'}
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 pr-4">
                          {u.role === 'admin' && <span className="text-xs px-2 py-0.5 rounded-sm bg-amber-500/20 text-amber-400">admin</span>}
                          {u.role !== 'admin' && <span className="text-xs text-zinc-500">user</span>}
                        </td>
                        <td className="py-2.5 text-zinc-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
